import { Box, Button, TextField, ToggleButton, ToggleButtonGroup, MenuItem, Typography, Alert } from '@mui/material';
import React, { useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { getLensSyncData, studentSync } from '../../../../../redux/features/lensSyncSlice';
import useHandleApiResponse from '../../../../../Hooks/useHandleApiResponse';

export default function StudentSyncModal({ row, onClose }) {
  const dispatch = useDispatch();
  const handleApiResponse = useHandleApiResponse();

  const { loading } = useSelector((state) => state.lens);
  const [type, setType] = React.useState('email');
  const [confirmed, setConfirmed] = React.useState(false);

  const isAlreadySynced = Boolean(row?.wise_student_id);

  const handleConfirm = () => {
    setConfirmed(true);
  };

  const initialValues = {
    email: row?.email || '',
    phone: row?.student_mob_no || '',
    countryCode: row?.a_phone_country_code || '+91',
    admnNo: row?.admn_no,
    alreadyMapped: row?.wise_student_id ? true : false,
  };

  const validationSchema = Yup.object({
    email: Yup.string().when('type', {
      is: 'email',
      then: (schema) => schema.email('Enter valid email address').required('Email is required'),
      otherwise: (schema) => schema.notRequired(),
    }),

    phone: Yup.string().when('type', {
      is: 'phone',
      then: (schema) =>
        schema.matches(/^[0-9]{6,14}$/, 'Enter valid phone number').required('Phone number is required'),
      otherwise: (schema) => schema.notRequired(),
    }),

    countryCode: Yup.string().when('type', {
      is: 'phone',
      then: (schema) => schema.required('Country code required'),
      otherwise: (schema) => schema.notRequired(),
    }),
  });
  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      console.log(values);

      const payload = {
        ...values,
        mobile: values.phone,
      };

      dispatch(studentSync(payload)).then((res) => {
        handleApiResponse(res, {
          onSuccess: () => {
            onClose();
          },
          onFailed: () => {
            console.log('student sync Failed');
          },
        });
      });
    },
  });

  if (!row) return null;

  return (
    <Box p={2}>
      <Typography fontWeight={600} mb={2}>
        Student: {row.student_name}
      </Typography>

      {!confirmed ? (
        <>
          {isAlreadySynced ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>Notice</strong>
              <br />
              This student is already synced with lens/wise .<br />
              <strong>Re-syncing will update the existing data .</strong>
            </Alert>
          ) : (
            <Alert severity="success" sx={{ mb: 2 }}>
              <strong>Disclaimer</strong>
              <br />
              This will sync the student with Lens/Wise.
              <br />
              <strong>If the student exists → data will be linked</strong>
              <br />
              <strong>If not → a new record will be created</strong>
            </Alert>
          )}

          <Box display="flex" justifyContent="flex-end" gap={2} mb={2}>
            <Button onClick={() => onClose(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleConfirm}>
              {isAlreadySynced ? 'Proceed with re-sync' : 'Continue'}
            </Button>
          </Box>
        </>
      ) : (
        <form onSubmit={formik.handleSubmit}>
          {/* TYPE TOGGLE */}
          <ToggleButtonGroup
            value={type}
            exclusive
            fullWidth
            onChange={(e, newValue) => {
              if (newValue) {
                setType(newValue);
                formik.setFieldValue('type', newValue);
                formik.resetForm();
              }
            }}
            sx={{ mb: 2 }}
          >
            <ToggleButton value="email">Email</ToggleButton>
            <ToggleButton value="phone">Phone</ToggleButton>
          </ToggleButtonGroup>

          {/* EMAIL */}
          {type === 'email' && (
            <TextField
              name="email"
              label="Lens Email"
              fullWidth
              margin="normal"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
            />
          )}

          {/* PHONE */}
          {type === 'phone' && (
            <Box display="flex" gap={2}>
              <TextField
                name="countryCode"
                label="Country Code"
                value={formik.values.countryCode}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.countryCode && Boolean(formik.errors.countryCode)}
                helperText={formik.touched.countryCode && formik.errors.countryCode}
                sx={{ width: '35%' }}
              />

              <TextField
                name="phone"
                label="Mobile Number"
                fullWidth
                value={formik.values.phone}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.phone && Boolean(formik.errors.phone)}
                helperText={formik.touched.phone && formik.errors.phone}
              />
            </Box>
          )}

          {/* ACTIONS */}
          <Box display="flex" justifyContent="flex-end" mt={3} gap={2}>
            <Button onClick={() => onClose(false)}>Cancel</Button>
            <Button variant="contained" type="submit" disabled={!formik.isValid}>
              Sync
            </Button>
          </Box>
        </form>
      )}
    </Box>
  );
}
